import { useEffect, useRef } from 'react';

interface PerformanceMetrics {
  timeToFirstByte: number;
  timeToFirstContentfulPaint: number;
  timeToLargestContentfulPaint: number;
  firstInputDelay: number;
}

/**
 * Performance monitoring hook for tracking key performance metrics
 */
export const usePerformanceMonitor = () => {
  const metricsRef = useRef<PerformanceMetrics>({
    timeToFirstByte: 0,
    timeToFirstContentfulPaint: 0,
    timeToLargestContentfulPaint: 0,
    firstInputDelay: 0,
  });

  useEffect(() => {
    // Track Time to First Byte (TTFB)
    const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navigationEntry) {
      metricsRef.current.timeToFirstByte = navigationEntry.responseStart - navigationEntry.requestStart;
    }

    // Track First Contentful Paint (FCP)
    const fcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const fcpEntry = entries[entries.length - 1];
      if (fcpEntry) {
        metricsRef.current.timeToFirstContentfulPaint = fcpEntry.startTime;
      }
    });
    fcpObserver.observe({ entryTypes: ['paint'] });

    // Track Largest Contentful Paint (LCP)
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lcpEntry = entries[entries.length - 1];
      if (lcpEntry) {
        metricsRef.current.timeToLargestContentfulPaint = lcpEntry.startTime;
      }
    });
    lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

    // Track First Input Delay (FID)
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        metricsRef.current.firstInputDelay = entry.processingStart - entry.startTime;
      });
    });
    fidObserver.observe({ entryTypes: ['first-input'] });

    // Log performance metrics (only in development, and only if metrics are slow)
    const logPerformanceMetrics = () => {
      const metrics = metricsRef.current;
      
      // Only log if performance is poor
      if (process.env.NODE_ENV === 'development' && 
          (metrics.timeToLargestContentfulPaint > 4000 || 
           metrics.firstInputDelay > 100)) {
        console.warn('⚠️ Performance Issues Detected:', {
          LCP: `${metrics.timeToLargestContentfulPaint.toFixed(0)}ms`,
          FID: `${metrics.firstInputDelay.toFixed(0)}ms`
        });
      }

      // Send metrics to analytics if needed
      if (window.gtag) {
        window.gtag('event', 'performance_metrics', {
          time_to_first_byte: metrics.timeToFirstByte,
          first_contentful_paint: metrics.timeToFirstContentfulPaint,
          largest_contentful_paint: metrics.timeToLargestContentfulPaint,
          first_input_delay: metrics.firstInputDelay,
        });
      }
    };

    // Log metrics after a delay to ensure all metrics are captured
    const timeoutId = setTimeout(logPerformanceMetrics, 5000);

    return () => {
      fcpObserver.disconnect();
      lcpObserver.disconnect();
      fidObserver.disconnect();
      clearTimeout(timeoutId);
    };
  }, []);

  return metricsRef.current;
};

export default usePerformanceMonitor; 